import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  message,
  Row,
  Spin,
  Statistic,
  Table,
  Tag,
} from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { BaseExcelService } from '../utils/excel/baseExcelService';
import * as ExcelJS from 'exceljs';
import { ExcelColors } from '../utils/excel/types';
import useEmployee from '../hook/useEmployee';
import { useIsAdmin } from '../hook/useRole';
import ProfitReportService, {
  FinancialRecordDetail,
  PaymentDetail,
  ProfitData,
  TransactionDetail,
} from '../service/profit-report-service/ProfitReport.service';

const { RangePicker } = DatePicker;

const defaultProfitData: ProfitData = {
  totalRevenue: 0,
  totalExpense: 0,
  netProfit: 0,
  maturityRevenue: {
    totalRevenue: 0,
    totalExpense: 0,
    netProfit: 0,
    paymentRevenue: 0,
    posRevenue: 0,
    posExpense: 0,
    relatedExpense: 0,
    paymentDetails: [],
    withdrawalDetails: [],
    depositDetails: [],
  },
  withdrawalRevenue: {
    totalRevenue: 0,
    totalExpense: 0,
    netProfit: 0,
    paymentRevenue: 0,
    posRevenue: 0,
    posExpense: 0,
    relatedExpense: 0,
    paymentDetails: [],
    withdrawalDetails: [],
    relatedExpenseDetails: [],
  },
  financialRecords: {
    totalRevenue: 0,
    totalExpense: 0,
    netProfit: 0,
    incomeDetails: [],
    expenseDetails: [],
  },
};

const ProfitReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [loading, setLoading] = useState(false);
  const [profitData, setProfitData] = useState<ProfitData>(defaultProfitData);

  const { employee } = useEmployee();
  const isAdmin = useIsAdmin();

  // Disable future dates
  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current > dayjs().endOf('day');
  };

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
      fetchProfitData(dates[0], dates[1]);
    }
  };

  const fetchProfitData = async (startDate: Dayjs, endDate: Dayjs) => {
    if (!employee?.id) return;

    setLoading(true);
    try {
      const data = await ProfitReportService.getProfitReport(
        startDate,
        endDate
      );
      setProfitData(data || defaultProfitData);

      // Hiển thị thông báo nếu có dữ liệu
      if (data && (data.totalRevenue > 0 || data.totalExpense > 0)) {
        message.success('Dữ liệu báo cáo đã được tải thành công');
      }
    } catch (error) {
      console.error('Error fetching profit data:', error);
      message.error('Có lỗi xảy ra khi tải dữ liệu báo cáo');
      setProfitData(defaultProfitData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employee?.id) {
      fetchProfitData(dateRange[0], dateRange[1]);
    }
  }, [employee?.id]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const renderPaymentTable = (payments: PaymentDetail[]) => {
    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={status === 'COMPLETED' ? 'green' : 'red'}>
            {status === 'COMPLETED' ? 'Đã thanh toán' : 'Chưa thanh toán'}
          </Tag>
        ),
      },
      {
        title: 'Số tiền',
        dataIndex: 'amount',
        key: 'amount',
        render: (amount: number) => formatCurrency(amount),
      },
      {
        title: 'Ngày thanh toán',
        dataIndex: 'paymentDate',
        key: 'paymentDate',
        render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      },
      {
        title: 'Thông tin kỳ đáo hạn',
        dataIndex: 'maturityScheduleId',
        key: 'maturityScheduleId',
        render: (maturityScheduleId: string) => {
          const payment = payments.find(
            (p) => p.maturityScheduleId === maturityScheduleId
          );
          if (payment) {
            return (
              <>
                {payment.maturitySchedule?.dueDate
                  ? dayjs(payment.maturitySchedule.dueDate).format('DD/MM/YYYY')
                  : 'N/A'}
                <br />
                {payment.maturitySchedule?.creditCard?.cardNumber || 'N/A'}
                <br />
                {payment.maturitySchedule?.creditCard?.cardHolderName || 'N/A'}
                <br />
                {payment.maturitySchedule?.creditCard?.bank?.name || 'N/A'}
              </>
            );
          }
          return 'N/A';
        },
      },
      {
        title: 'Nhân viên',
        dataIndex: 'employee',
        key: 'employee',
        render: (_: any, record: PaymentDetail) =>
          record.employee?.name || 'N/A',
      },
    ];

    return <Table columns={columns} dataSource={payments} rowKey="id" />;
  };

  const renderTransactionTable = (transactions: TransactionDetail[]) => {
    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={status === 'COMPLETED' ? 'green' : 'red'}>
            {status === 'COMPLETED' ? 'Đã hoàn thành' : 'Chưa hoàn thành'}
          </Tag>
        ),
      },
      {
        title: 'Loại giao dịch',
        dataIndex: 'transactionType',
        key: 'transactionType',
        render: (type: string) => (
          <Tag color={type === 'withdrawal' ? 'blue' : 'orange'}>
            {type === 'withdrawal' ? 'Rút tiền' : 'Nạp tiền'}
          </Tag>
        ),
      },
      {
        title: 'Số tiền',
        dataIndex: 'amount',
        key: 'amount',
        render: (amount: number) => formatCurrency(amount),
      },
      {
        title: 'Số dư',
        dataIndex: 'balance',
        key: 'balance',
        render: (balance: number) => formatCurrency(balance),
      },
      {
        title: 'Ngày giao dịch',
        dataIndex: 'transactionDate',
        key: 'transactionDate',
        render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      },
      {
        title: 'Thông tin thẻ',
        dataIndex: 'cardNumber',
        key: 'cardNumber',
        render: (_: string, record: TransactionDetail) => (
          <>
            {record.cardNumber || record.creditCard?.cardNumber || 'N/A'}
            <br />
            {record.creditCard?.cardHolderName || 'N/A'}
            <br />
            {record.creditCard?.bank?.name || 'N/A'}
            <br />
            {record.creditCard?.cardType || 'N/A'}
          </>
        ),
      },
      {
        title: 'Nhân viên',
        dataIndex: 'employee',
        key: 'employee',
        render: (_: any, record: TransactionDetail) =>
          record.employee?.name || 'N/A',
      },
    ];

    return <Table columns={columns} dataSource={transactions} rowKey="id" />;
  };

  const renderFinancialRecordTable = (records: FinancialRecordDetail[]) => {
    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'Loại',
        dataIndex: 'recordType',
        key: 'recordType',
        render: (type: string) => (
          <Tag color={type === 'INCOME' ? 'green' : 'red'}>
            {type === 'INCOME' ? 'Thu' : 'Chi'}
          </Tag>
        ),
      },
      {
        title: 'Số tiền',
        dataIndex: 'amount',
        key: 'amount',
        render: (amount: number) => formatCurrency(amount),
      },
      {
        title: 'Ngày',
        dataIndex: 'recordDate',
        key: 'recordDate',
        render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      },
      {
        title: 'Mô tả',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: 'Nhân viên',
        dataIndex: 'employee',
        key: 'employee',
        render: (_: any, record: PaymentDetail | FinancialRecordDetail) =>
          record.employee?.name || 'N/A',
      },
    ];

    return <Table columns={columns} dataSource={records} rowKey="id" />;
  };

  const handleExportFullReport = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Báo cáo tổng hợp');
    let rowIdx = 1;

    // Tiêu đề lớn
    ws.mergeCells(`A${rowIdx}:K${rowIdx}`);
    BaseExcelService.createHeaderCell(
      ws,
      `A${rowIdx}`,
      'BÁO CÁO DOANH THU',
      18,
      ExcelColors.PRIMARY_HEADER
    );
    rowIdx += 2;

    // Tổng quan
    ws.getCell(`A${rowIdx}`).value = 'Tổng doanh thu';
    ws.getCell(`B${rowIdx}`).value = formatCurrency(profitData.totalRevenue);
    BaseExcelService.applyCellStyle(ws.getCell(`A${rowIdx}`), {
      font: { bold: true },
      alignment: { vertical: 'middle', horizontal: 'center' },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF5F5F5' },
      },
    });
    BaseExcelService.applyCellStyle(ws.getCell(`B${rowIdx}`), {
      alignment: { vertical: 'middle', horizontal: 'center' },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF5F5F5' },
      },
    });
    rowIdx++;
    BaseExcelService.applyCellStyle(ws.getCell(`A${rowIdx}`), {
      font: { bold: true },
      alignment: { vertical: 'middle', horizontal: 'center' },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: ExcelColors.TABLE_HEADER },
      },
    });
    rowIdx++;
    ws.getCell(`A${rowIdx}`).value = 'Tổng chi phí';
    ws.getCell(`B${rowIdx}`).value = formatCurrency(profitData.totalExpense);
    BaseExcelService.applyCellStyle(ws.getCell(`A${rowIdx}`), {
      font: { bold: true },
      alignment: { vertical: 'middle', horizontal: 'center' },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: ExcelColors.TABLE_HEADER },
      },
    });
    rowIdx++;
    ws.getCell(`A${rowIdx}`).value = 'Lợi nhuận ròng';
    ws.getCell(`B${rowIdx}`).value = formatCurrency(profitData.netProfit);
    BaseExcelService.applyCellStyle(ws.getCell(`A${rowIdx}`), {
      font: { bold: true },
      alignment: { vertical: 'middle', horizontal: 'center' },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: ExcelColors.TABLE_HEADER },
      },
    });
    rowIdx += 2;

    // --- Bảng thanh toán ---
    ws.mergeCells(`A${rowIdx}:K${rowIdx}`);
    BaseExcelService.createHeaderCell(
      ws,
      `A${rowIdx}`,
      'CHI TIẾT THANH TOÁN',
      14,
      ExcelColors.PRIMARY_HEADER
    );
    rowIdx++;
    const paymentHeaders = [
      'ID',
      'Trạng thái',
      'Số tiền',
      'Ngày thanh toán',
      'Kỳ đáo hạn',
      'Số thẻ',
      'Chủ thẻ',
      'Ngân hàng',
      'Loại thẻ',
      'Khách hàng',
      'Nhân viên',
    ];
    BaseExcelService.createTableHeader(ws, rowIdx, paymentHeaders);
    rowIdx++;
    profitData.maturityRevenue.paymentDetails.forEach((p) => {
      const row = ws.getRow(rowIdx);
      row.values = [
        p.id,
        p.status === 'COMPLETED' ? 'Đã hoàn thành' : 'Chưa hoàn thành',
        formatCurrency(p.amount),
        p.paymentDate ? dayjs(p.paymentDate).format('DD/MM/YYYY HH:mm') : '',
        p.maturitySchedule?.dueDate
          ? dayjs(p.maturitySchedule.dueDate).format('DD/MM/YYYY')
          : '',
        p.maturitySchedule?.creditCard?.cardNumber || '',
        p.maturitySchedule?.creditCard?.cardHolderName || '',
        p.maturitySchedule?.creditCard?.bank?.name || '',
        p.maturitySchedule?.creditCard?.cardType || '',
        p.customer?.fullName || '',
        p.employee?.name || '',
      ];
      row.eachCell((cell: ExcelJS.Cell) => {
        BaseExcelService.applyBorder(cell);
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
      rowIdx++;
    });
    rowIdx += 2;

    // --- Bảng giao dịch rút tiền ---
    ws.mergeCells(`A${rowIdx}:L${rowIdx}`);
    BaseExcelService.createHeaderCell(
      ws,
      `A${rowIdx}`,
      'CHI TIẾT GIAO DỊCH RÚT TIỀN',
      14,
      ExcelColors.PRIMARY_HEADER
    );
    rowIdx++;
    const withdrawalHeaders = [
      'ID',
      'Trạng thái',
      'Loại giao dịch',
      'Số tiền',
      'Số dư',
      'Ngày giao dịch',
      'Số thẻ',
      'Chủ thẻ',
      'Ngân hàng',
      'Loại thẻ',
      'Khách hàng',
      'Nhân viên',
    ];
    BaseExcelService.createTableHeader(ws, rowIdx, withdrawalHeaders);
    rowIdx++;
    profitData.maturityRevenue.withdrawalDetails.forEach((t) => {
      const row = ws.getRow(rowIdx);
      row.values = [
        t.id,
        t.status === 'COMPLETED' ? 'Đã hoàn thành' : 'Chưa hoàn thành',
        t.transactionType === 'withdrawal' ? 'Rút tiền' : t.transactionType,
        formatCurrency(t.amount),
        formatCurrency(t.balance),
        t.transactionDate
          ? dayjs(t.transactionDate).format('DD/MM/YYYY HH:mm')
          : '',
        t.cardNumber || t.creditCard?.cardNumber || '',
        t.creditCard?.cardHolderName || '',
        t.creditCard?.bank?.name || '',
        t.creditCard?.cardType || '',
        t.customer?.fullName || '',
        t.employee?.name || '',
      ];
      row.eachCell((cell: ExcelJS.Cell) => {
        BaseExcelService.applyBorder(cell);
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
      rowIdx++;
    });
    rowIdx += 2;

    // --- Bảng giao dịch nạp tiền ---
    ws.mergeCells(`A${rowIdx}:L${rowIdx}`);
    BaseExcelService.createHeaderCell(
      ws,
      `A${rowIdx}`,
      'CHI TIẾT GIAO DỊCH NẠP TIỀN',
      14,
      ExcelColors.PRIMARY_HEADER
    );
    rowIdx++;
    const depositHeaders = [
      'ID',
      'Trạng thái',
      'Loại giao dịch',
      'Số tiền',
      'Số dư',
      'Ngày giao dịch',
      'Số thẻ',
      'Chủ thẻ',
      'Ngân hàng',
      'Loại thẻ',
      'Khách hàng',
      'Nhân viên',
    ];
    BaseExcelService.createTableHeader(ws, rowIdx, depositHeaders);
    rowIdx++;
    profitData.maturityRevenue.depositDetails.forEach((t) => {
      const row = ws.getRow(rowIdx);
      row.values = [
        t.id,
        t.status === 'COMPLETED' ? 'Đã hoàn thành' : 'Chưa hoàn thành',
        t.transactionType === 'deposit' ? 'Nạp tiền' : t.transactionType,
        formatCurrency(t.amount),
        formatCurrency(t.balance),
        t.transactionDate
          ? dayjs(t.transactionDate).format('DD/MM/YYYY HH:mm')
          : '',
        t.cardNumber || t.creditCard?.cardNumber || '',
        t.creditCard?.cardHolderName || '',
        t.creditCard?.bank?.name || '',
        t.creditCard?.cardType || '',
        t.customer?.fullName || '',
        t.employee?.name || '',
      ];
      row.eachCell((cell: ExcelJS.Cell) => {
        BaseExcelService.applyBorder(cell);
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
      rowIdx++;
    });
    rowIdx += 2;

    // --- Bảng chi tiết chi phí liên quan (nếu có) ---
    if (
      profitData.withdrawalRevenue.relatedExpenseDetails &&
      profitData.withdrawalRevenue.relatedExpenseDetails.length > 0
    ) {
      ws.mergeCells(`A${rowIdx}:G${rowIdx}`);
      BaseExcelService.createHeaderCell(
        ws,
        `A${rowIdx}`,
        'CHI TIẾT CHI PHÍ LIÊN QUAN',
        14,
        ExcelColors.PRIMARY_HEADER
      );
      rowIdx++;
      const expenseHeaders = [
        'ID',
        'Loại',
        'Số tiền',
        'Ngày',
        'Mô tả',
        'Giao dịch liên quan',
        'Nhân viên',
      ];
      BaseExcelService.createTableHeader(ws, rowIdx, expenseHeaders);
      rowIdx++;
      profitData.withdrawalRevenue.relatedExpenseDetails.forEach((r) => {
        const row = ws.getRow(rowIdx);
        row.values = [
          r.id,
          r.recordType === 'expense' ? 'Chi' : 'Thu',
          formatCurrency(r.amount),
          r.recordDate ? dayjs(r.recordDate).format('DD/MM/YYYY HH:mm') : '',
          r.description || '',
          r.relatedTransactionId || '',
          r.employee?.name || '',
        ];
        row.eachCell((cell: ExcelJS.Cell) => {
          BaseExcelService.applyBorder(cell);
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });
        rowIdx++;
      });
      rowIdx += 2;
    }

    // --- Bảng thu chi ---
    ws.mergeCells(`A${rowIdx}:F${rowIdx}`);
    BaseExcelService.createHeaderCell(
      ws,
      `A${rowIdx}`,
      'CHI TIẾT THU CHI',
      14,
      ExcelColors.PRIMARY_HEADER
    );
    rowIdx++;
    const thuChiHeaders = [
      'ID',
      'Loại',
      'Số tiền',
      'Ngày',
      'Mô tả',
      'Nhân viên',
    ];
    BaseExcelService.createTableHeader(ws, rowIdx, thuChiHeaders);
    rowIdx++;
    profitData.financialRecords.incomeDetails.forEach((r) => {
      const row = ws.getRow(rowIdx);
      row.values = [
        r.id,
        'Thu',
        formatCurrency(r.amount),
        r.recordDate ? dayjs(r.recordDate).format('DD/MM/YYYY HH:mm') : '',
        r.description || '',
        r.employee?.name || '',
      ];
      row.eachCell((cell: ExcelJS.Cell) => {
        BaseExcelService.applyBorder(cell);
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
      rowIdx++;
    });
    profitData.financialRecords.expenseDetails.forEach((r) => {
      const row = ws.getRow(rowIdx);
      row.values = [
        r.id,
        'Chi',
        formatCurrency(r.amount),
        r.recordDate ? dayjs(r.recordDate).format('DD/MM/YYYY HH:mm') : '',
        r.description || '',
        r.employee?.name || '',
      ];
      row.eachCell((cell: ExcelJS.Cell) => {
        BaseExcelService.applyBorder(cell);
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
      rowIdx++;
    });

    // Auto-fit columns
    ws.columns.forEach((col) => {
      let maxLength = 10;
      if (col && typeof col.eachCell === 'function') {
        col.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell) => {
          const cellValue = cell.value ? cell.value.toString() : '';
          if (cellValue.length > maxLength) maxLength = cellValue.length;
        });
        col.width = Math.min(maxLength + 2, 30);
      }
    });

    await BaseExcelService.saveExcelFile(
      workbook,
      BaseExcelService.generateFileName('Bao_cao_tong_hop')
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Card className="shadow-md">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">
            Báo cáo lợi nhuận
          </h1>
          <div className="flex items-center gap-4">
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              disabledDate={disabledDate}
              format="DD/MM/YYYY"
            />
            <Button
              type="default"
              className="bg-green-500 text-white"
              icon={<DownloadOutlined />}
              onClick={handleExportFullReport}
            >
              Xuất Excel tổng hợp
            </Button>
          </div>
        </div>

        <Spin spinning={loading}>
          <div className="space-y-6">
            {/* Tổng quan */}
            <Card title="Tổng quan">
              <Row gutter={16} className="bg-indigo-50 p-4 rounded-lg">
                <Col span={8}>
                  <Statistic
                    title="Tổng doanh thu"
                    value={profitData?.totalRevenue || 0}
                    precision={0}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<ArrowUpOutlined />}
                    formatter={(value) => formatCurrency(value as number)}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Tổng chi phí"
                    value={profitData?.totalExpense || 0}
                    precision={0}
                    valueStyle={{ color: '#cf1322' }}
                    prefix={<ArrowDownOutlined />}
                    formatter={(value) => formatCurrency(value as number)}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Lợi nhuận ròng"
                    value={profitData?.netProfit || 0}
                    precision={0}
                    valueStyle={{
                      color:
                        (profitData?.netProfit || 0) >= 0
                          ? '#3f8600'
                          : '#cf1322',
                    }}
                    prefix={
                      (profitData?.netProfit || 0) >= 0 ? (
                        <ArrowUpOutlined />
                      ) : (
                        <ArrowDownOutlined />
                      )
                    }
                    formatter={(value) => formatCurrency(value as number)}
                  />
                </Col>
              </Row>
            </Card>

            {/* Chi tiết doanh thu đáo hạn */}
            <Card title="Chi tiết doanh thu đáo hạn">
              <Row gutter={16} className="bg-indigo-50 p-4 rounded-lg">
                <Col span={12}>
                  <h3 className="text-lg font-semibold mb-4">Doanh thu</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Doanh thu từ các thanh toán:</span>
                      <span className="font-medium">
                        {formatCurrency(
                          profitData.maturityRevenue.paymentRevenue
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Số rút:</span>
                      <span className="font-medium">
                        {formatCurrency(profitData.maturityRevenue.posRevenue)}
                      </span>
                    </div>
                    <Divider />
                    <div className="flex justify-between font-semibold">
                      <span>Tổng doanh thu:</span>
                      <span>
                        {formatCurrency(
                          profitData.maturityRevenue.totalRevenue
                        )}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <h3 className="text-lg font-semibold mb-4">Chi phí</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Chi phí POS:</span>
                      <span className="font-medium">
                        {formatCurrency(profitData.maturityRevenue.posExpense)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Chi phí liên quan:</span>
                      <span className="font-medium">
                        {formatCurrency(
                          profitData.maturityRevenue.relatedExpense
                        )}
                      </span>
                    </div>
                    <Divider />
                    <div className="flex justify-between font-semibold">
                      <span>Tổng chi phí:</span>
                      <span>
                        {formatCurrency(
                          profitData.maturityRevenue.totalExpense
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Lợi nhuận:</span>
                      <span
                        className={
                          profitData.maturityRevenue.netProfit >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }
                      >
                        {formatCurrency(profitData.maturityRevenue.netProfit)}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
              <Divider />
              <h3 className="text-lg font-semibold mb-4">
                Chi tiết thanh toán
              </h3>
              {renderPaymentTable(profitData.maturityRevenue.paymentDetails)}
              <Divider />
              <h3 className="text-lg font-semibold mb-4">
                Chi tiết giao dịch rút tiền
              </h3>
              {renderTransactionTable(
                profitData.maturityRevenue.withdrawalDetails
              )}
              <Divider />
              <h3 className="text-lg font-semibold mb-4">
                Chi tiết giao dịch nạp tiền
              </h3>
              {renderTransactionTable(
                profitData.maturityRevenue.depositDetails
              )}
            </Card>

            {/* Chi tiết doanh thu rút tiền */}
            <Card title="Chi tiết doanh thu rút tiền">
              <Row gutter={16} className="bg-indigo-50 p-4 rounded-lg">
                <Col span={12}>
                  <h3 className="text-lg font-semibold mb-4">Doanh thu</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Doanh thu từ các thanh toán:</span>
                      <span className="font-medium">
                        {formatCurrency(
                          profitData.withdrawalRevenue.paymentRevenue
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Số rút:</span>
                      <span className="font-medium">
                        {formatCurrency(
                          profitData.withdrawalRevenue.posRevenue
                        )}
                      </span>
                    </div>
                    <Divider />
                    <div className="flex justify-between font-semibold">
                      <span>Tổng doanh thu:</span>
                      <span>
                        {formatCurrency(
                          profitData.withdrawalRevenue.totalRevenue
                        )}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <h3 className="text-lg font-semibold mb-4">Chi phí</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Chi phí POS:</span>
                      <span className="font-medium">
                        {formatCurrency(
                          profitData.withdrawalRevenue.posExpense
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Chi phí liên quan:</span>
                      <span className="font-medium">
                        {formatCurrency(
                          profitData.withdrawalRevenue.relatedExpense
                        )}
                      </span>
                    </div>
                    <Divider />
                    <div className="flex justify-between font-semibold">
                      <span>Tổng chi phí:</span>
                      <span>
                        {formatCurrency(
                          profitData.withdrawalRevenue.totalExpense
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Lợi nhuận:</span>
                      <span
                        className={
                          profitData.withdrawalRevenue.netProfit >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }
                      >
                        {formatCurrency(profitData.withdrawalRevenue.netProfit)}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
              <Divider />
              <h3 className="text-lg font-semibold mb-4">
                Chi tiết thanh toán
              </h3>
              {renderPaymentTable(profitData.withdrawalRevenue.paymentDetails)}
              <Divider />
              <h3 className="text-lg font-semibold mb-4">
                Chi tiết giao dịch rút tiền
              </h3>
              {renderTransactionTable(
                profitData.withdrawalRevenue.withdrawalDetails
              )}
              <Divider />
              <h3 className="text-lg font-semibold mb-4">
                Chi tiết chi phí liên quan
              </h3>
              {renderFinancialRecordTable(
                profitData.withdrawalRevenue.relatedExpenseDetails
              )}
            </Card>

            {/* Chi tiết bản ghi tài chính */}
            <Card title="Chi tiết thu chi">
              <Row gutter={16} className="bg-indigo-50 p-4 rounded-lg">
                <Col span={12}>
                  <h3 className="text-lg font-semibold mb-4">Thu nhập</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between font-semibold">
                      <span>Tổng thu:</span>
                      <span>
                        {formatCurrency(
                          profitData.financialRecords.totalRevenue
                        )}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <h3 className="text-lg font-semibold mb-4">Chi phí</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between font-semibold">
                      <span>Tổng chi phí:</span>
                      <span>
                        {formatCurrency(
                          profitData.financialRecords.totalExpense
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Lợi nhuận:</span>
                      <span
                        className={
                          profitData.financialRecords.netProfit >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }
                      >
                        {formatCurrency(profitData.financialRecords.netProfit)}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
              <Divider />
              <h3 className="text-lg font-semibold mb-4">Chi tiết thu nhập</h3>
              {renderFinancialRecordTable(
                profitData.financialRecords.incomeDetails
              )}
              <Divider />
              <h3 className="text-lg font-semibold mb-4">Chi tiết chi phí</h3>
              {renderFinancialRecordTable(
                profitData.financialRecords.expenseDetails
              )}
            </Card>
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default ProfitReport;
